import { useState, useEffect } from 'react';
import axios from 'axios';
import { Axe, Check, AlertTriangle } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ElagagePage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageRes = await axios.get(`${API}/pages/elagage`);
        setPageSettings(pageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    "Élagage d'arbres de toutes tailles",
    "Taille de formation",
    "Taille d'entretien et réduction",
    "Abattage d'arbres",
    "Dessouchage",
    "Évacuation des déchets verts"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-amber-50 to-white py-12 lg:py-20 pt-28 lg:pt-36" data-testid="elagage-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-amber-100 text-amber-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Axe className="w-4 h-4 inline mr-2" />
              Élagage
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-4">
              {pageSettings?.title || "Élagage d'arbres"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Service professionnel d'élagage et d'abattage d'arbres. Intervention sécurisée par des experts qualifiés."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="elagage-content">
        <div className="container-custom">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - First on mobile */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Demander un devis élagage
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de devis sera configuré dans le back-office"
                />
              </div>
            </div>

            {/* Services - Second on mobile */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Nos services d'élagage
              </h2>
              
              <div className="bg-amber-50 rounded-2xl p-6 mb-8 border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-semibold mb-1">Intervention sécurisée</p>
                    <p className="text-stone-600 text-sm">
                      Nos élagueurs sont certifiés et équipés pour intervenir en toute sécurité, même sur les arbres les plus difficiles d'accès.
                    </p>
                  </div>
                </div>
              </div>

              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-stone-700">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-4 bg-stone-100 rounded-xl">
                <p className="text-stone-600 text-sm">
                  <strong>Note :</strong> Pour les arbres de grande hauteur ou les situations complexes, 
                  nous effectuons une visite préalable gratuite pour établir un devis précis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-amber-600" data-testid="elagage-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            Besoin d'un élagage urgent ?
          </h2>
          <p className="text-amber-100 mb-6">
            Nous intervenons rapidement pour sécuriser votre propriété.
          </p>
          <a 
            href="tel:0465011022" 
            className="inline-flex items-center gap-2 bg-white text-amber-600 font-semibold px-8 py-4 rounded-full hover:bg-stone-100 transition-colors text-lg"
          >
            04 65 01 10 22
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ElagagePage;
